import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FeatureGroup, MapContainer, Marker, Polyline, TileLayer, useMap, useMapEvents, } from "react-leaflet";
import { FullscreenControl } from "react-leaflet-fullscreen";
import "react-leaflet-fullscreen/styles.css";
import "leaflet-rotatedmarker";
import "./Map.css";
import { AddPointButton } from "./AddPointButton/AddPointButton";
import { DragMapHandler } from "./DragMapHandler/DragMapHandler";
import { MapContextMenu } from "./MapContextMenu/MapContextMenu";
import { RecenterButton } from "./RecenterButton/RecenterButton";
import { RightClickMapHandler } from "./RightClickMapHandler/RightClickMapHandler";
import { ViewSwitchButton } from "./ViewSwitchButton/ViewSwitchButton";
import missionService from "../../services/missionService";
const flagSvg = '<?xml version="1.0" encoding="iso-8859-1"?><svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 32 32" xml:space="preserve"><polyline style="fill:none;stroke:#000000;stroke-width:2;stroke-miterlimit:10;" points="6,28 6,5 26,5 26,19 6,19 "/><rect x="22" y="5" width="4" height="4"/><rect x="19" y="15" width="3" height="4"/><rect x="19" y="9" width="3" height="3"/><rect x="13" y="15" width="3" height="4"/><rect x="13" y="9" width="3" height="3"/><rect x="6" y="15" width="4" height="4"/><rect x="6" y="9" width="4" height="3"/><rect x="22" y="12" width="4" height="3"/><rect x="16" y="12" width="3" height="3"/><rect x="10" y="12" width="3" height="3"/><rect x="16" y="5" width="3" height="4"/><rect x="10" y="5" width="3" height="4"/></svg>';
const markerSvgString = '<svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"\t viewBox="0 0 488.98 488.98" style="enable-background:new 0 0 488.98 488.98;" xml:space="preserve"><g>\t<g>\t\t<path d="M409.49,485.75l-165-80.9l-165,80.9c-25.3,12.4-52.4-13.1-41.6-39.1l178.5-427.9c10.4-25,45.8-25,56.3,0l178.4,427.9\t\t\tC461.89,472.55,434.79,498.15,409.49,485.75z"/>\t</g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g></svg>';
const markerUrl = encodeURI("data:image/svg+xml," + markerSvgString).replace("#", "%23");
const startUrl = encodeURI("data:image/svg+xml," + flagSvg).replace("#", "%23");
const ChangeView = ({ center }) => {
    const map = useMap();
    map.setView(center);
    return null;
};
const FullscreenWatcher = ({ onChange }) => {
    useMapEvents({
        enterFullscreen: () => onChange(true),
        exitFullscreen: () => onChange(false),
    });
    return null;
};
export const Map = ({ lat, lng, speed, azimuth, routePoints, destinationPoints, missionId, isOnline = true, setSelectedLayer, selectedLayer, }) => {
    var _a, _b, _c;
    const { t } = useTranslation();
    const ref = useRef();
    const [markerIcon, setMarkerIcon] = useState((_a = window.L) === null || _a === void 0 ? void 0 : _a.icon({
        iconUrl: markerUrl,
        iconSize: [20, 20],
    }));
    const [startIcon] = useState((_b = window.L) === null || _b === void 0 ? void 0 : _b.icon({
        iconUrl: startUrl,
        iconSize: [20, 20],
    }));
    const [destinationIcon] = useState((_c = window.L) === null || _c === void 0 ? void 0 : _c.icon({
        iconUrl: "https://unpkg.com/leaflet@1.6.0/dist/images/marker-icon-2x.png",
        iconSize: [20, 30],
    }));
    const [start, setStart] = useState(undefined);
    const [finish, setFinish] = useState(undefined);
    const [current, setCurrent] = useState(undefined);
    const currentRef = useRef(undefined);
    const [center, setCenter] = useState([0, 0]);
    const [menuPosition, setMenuPosition] = useState(null);
    const [destinations, setDestinations] = useState([]);
    const [isMapMoved, setIsMapMoved] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const menuItems = {
        [t("map.contextMenu.addPoint")]: async () => {
            if (menuPosition) {
                await addDestination(menuPosition.latlng);
            }
        },
        [t("map.contextMenu.copyCoordinates")]: async () => {
            if (menuPosition) {
                await copyCoordinates(menuPosition.latlng);
            }
        },
    };
    useEffect(() => {
        currentRef.current = current;
    }, [current]);
    useEffect(() => {
        if (destinationPoints) {
            setDestinations(destinationPoints);
        }
    }, [destinationPoints]);
    const addDestination = async (point) => {
        if (missionId) {
            setDestinations([...destinations, point]);
            setIsMapMoved(true);
            await missionService.addDestinationPoint(missionId, {
                latitude: point.lat,
                longitude: point.lng,
            });
        }
    };
    const copyCoordinates = async (point) => {
        const coords = point.lat + ", " + point.lng;
        // Copy to clipboard using modern API
        navigator.clipboard.writeText(coords);
    };
    useEffect(() => {
        if (isOnline && lat && lng) {
            setCurrent([lat, lng]);
            if (!isMapMoved) {
                setCenter([lat, lng]);
            }
        }
    }, [isOnline, lat, lng]);
    useEffect(() => {
        if (!isOnline) {
            if (routePoints && routePoints.length > 0) {
                const len = routePoints.length;
                setStart([
                    routePoints[0].lat,
                    routePoints[0].lng,
                ]);
                setFinish([
                    routePoints[len - 1].lat,
                    routePoints[len - 1].lng,
                ]);
                setCenter([
                    routePoints[len - 1].lat,
                    routePoints[len - 1].lng,
                ]);
            }
            else {
                if (destinationPoints && destinationPoints.length > 0) {
                    const len = destinationPoints.length;
                    setCenter([
                        destinationPoints[len - 1].lat,
                        destinationPoints[len - 1].lng,
                    ]);
                }
            }
        }
    }, [isOnline, routePoints]);
    useEffect(() => {
        if (ref.current) {
            if (!speed) {
                ref.current.setRotationAngle(0);
            }
            else {
                ref.current.setRotationAngle(azimuth);
            }
        }
    }, [azimuth, speed]);
    useEffect(() => {
        var _a, _b;
        if (!speed) {
            setMarkerIcon((_a = window.L) === null || _a === void 0 ? void 0 : _a.icon({
                iconUrl: "https://unpkg.com/leaflet@1.6.0/dist/images/marker-icon-2x.png",
                iconSize: [20, 30],
            }));
        }
        else {
            setMarkerIcon((_b = window.L) === null || _b === void 0 ? void 0 : _b.icon({
                iconUrl: markerUrl,
                iconSize: [20, 20],
            }));
        }
    }, [speed]);
    const menuRef = useRef(null);
    useEffect(() => {
        const handleClick = (e) => {
            if (menuRef.current &&
                !menuRef.current.contains(e.target)) {
                setMenuPosition(null); // Клик вне меню → закрываем
            }
        };
        document.addEventListener("click", handleClick);
        return () => {
            document.removeEventListener("click", handleClick);
        };
    }, []);
    const recenter = () => {
        if (currentRef.current) {
            setCenter(currentRef.current);
        }
        setIsMapMoved(false);
    };
    const changeMap = () => {
        const mapsCount = 3;
        if (setSelectedLayer) {
            setSelectedLayer((prev) => (prev + 1) % mapsCount);
        }
    };
    return (_jsx("div", { children: _jsxs(MapContainer, { center: center, zoom: 17, style: { width: "100%", height: "100%", position: "absolute" }, children: [!isMapMoved ? _jsx(ChangeView, { center: center }) : null, selectedLayer === 0 && (_jsx(TileLayer, { url: "https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", maxZoom: 20, subdomains: ["mt1", "mt2", "mt3"] })), selectedLayer === 1 && (_jsx(TileLayer, { url: "https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", maxZoom: 20, subdomains: ["mt1", "mt2", "mt3"] })), selectedLayer === 3 && (_jsx(TileLayer, { attribution: '\u00A9 <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors', url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" })), selectedLayer === 2 && (_jsx(TileLayer, { url: "https://tile.opentopomap.org/{z}/{x}/{y}.png" })), missionId ? (_jsx(RightClickMapHandler, { setMenuPosition: setMenuPosition })) : null, _jsx(ViewSwitchButton, { onClick: changeMap }), _jsx(DragMapHandler, { setDragState: (state) => setIsMapMoved(state) }), destinations.map((pos, index) => (_jsx(Marker, { position: pos, icon: destinationIcon }, index))), start ? (_jsx(Marker, { ref: ref, rotationAngle: azimuth, position: start, icon: startIcon })) : null, current ? (_jsx(Marker, { ref: ref, rotationAngle: azimuth, position: current, icon: markerIcon })) : null, finish ? _jsx(Marker, { position: finish, icon: markerIcon }) : null, routePoints ? (_jsx(FeatureGroup, { children: _jsx(Polyline, { positions: routePoints, color: "red" }) })) : null, _jsx(FullscreenControl, { position: "topright", forceSeparateButton: true }), _jsx(FullscreenWatcher, { onChange: setIsFullscreen }), isOnline ? _jsx(RecenterButton, { onClick: () => recenter() }) : null, _jsx(MapContextMenu, { items: menuItems, position: menuPosition, onClose: () => setMenuPosition(null), containerRef: menuRef }), missionId && (_jsx("div", { className: `${isFullscreen ? "is-fullscreen-mode" : "is-normal-mode"}`, children: _jsx(AddPointButton, { onClick: addDestination }) }))] }) }));
};
